import type Cookies from 'cookies-js'
import type { DefaultContext } from 'koa'
import moment from 'moment'

import { isServer } from '@library/environment'

import { canUseWindow } from './browserEnvironment'
import { cookieString } from './cookieString'
import logger from './logger'

const versionPrefix = 'v1'

function getPrefixedKey(key: string) {
  return `${versionPrefix}_${key}`
}

export function encode(
  val: Record<string, unknown> | string | number | boolean | null | undefined,
): string {
  let ret = ''

  if (canUseWindow()) {
    ret = val ? JSON.stringify(val) : ''
  }

  if (isServer()) {
    ret = val ? encodeURIComponent(JSON.stringify(val)) : ''
  }

  return ret
}

export function decode(val?: string) {
  let ret

  if (isServer()) {
    ret = val ? JSON.parse(decodeURIComponent(val)) : undefined
  }

  if (canUseWindow()) {
    ret = val ? JSON.parse(val) : undefined
  }

  return ret
}

export function get<T>(
  cookies: typeof Cookies,
  key: string,
  withVersionPrefix = true,
  shouldDecode = true,
): T | null | undefined {
  const fullKey = withVersionPrefix ? getPrefixedKey(key) : key

  if (cookies && typeof cookies.get === 'function') {
    try {
      if (key === 'oauth_token' || key === 'oauth_refresh') {
        if (cookies.get(fullKey) === 'deleted') {
          return null
        }
      }

      const cookieValue = cookies.get(fullKey)

      return shouldDecode ? decode(cookieValue) : cookieValue
    } catch (err) {
      logger.notice(`un-parsable cookie value for key: ${key}, value: ${cookies.get(key)}`)
      logger.critical({
        message: `un-parsable cookie value for key: ${key}`,
        errors: [err],
      })
    }
  }

  return undefined
}

export function set(
  cookies: typeof Cookies,
  key: string,
  val: Record<string, unknown> | string | number | boolean | null | undefined,
  days: number,
  withVersionPrefix = true,
  path = '/',
  domain: string,
) {
  const prefixedKey = withVersionPrefix ? getPrefixedKey(key) : key

  if (cookies && typeof cookies.set === 'function') {
    if (days) {
      const expires = moment().add(days * 24, 'hours')
      cookies.set(prefixedKey, encode(val), {
        expires: expires.toDate(),
        path,
        domain,
      })
    } else {
      cookies.set(prefixedKey, encode(val), { path, domain })
    }
  } else {
    logger.error({ message: 'no cookies to set on' })
  }
}

export function unset(
  cookies: typeof Cookies,
  key: string,
  withVersionPrefix = true,
  path = '/',
  domain?: string,
) {
  const prefixedKey = withVersionPrefix ? getPrefixedKey(key) : key

  if (cookies && typeof cookies.set === 'function') {
    cookies.set(prefixedKey, null, {
      expires: new Date('1970-01-01'),
      path,
      domain,
    })
  } else {
    logger.error({ message: 'no cookies to delete on' })
  }
}

export function getNamesWithPrefix(reqCookies: DefaultContext, prefix: string) {
  const cookies = cookieString(reqCookies)
  let cookieNames: string[] = []
  if (cookies) {
    const match = RegExp('^\\s*' + getPrefixedKey('') + prefix + '(.*?)=\\s*(.*?)\\s*$') //eslint-disable-line

    let index
    const matches = []
    for (index = 0; index < cookies.length; index++) {
      const entry = cookies[index].match(match)
      if (entry !== null) {
        matches.push(entry[1])
      }
    }

    cookieNames = matches.map((cookieName) => `${prefix}${cookieName}`)
  }

  return cookieNames
}
